import React, { useEffect, useState } from "react";

import {
  FaArrowLeft,
  FaBoxOpen,
  FaImage,
  FaVideo,
  FaTimes,
} from "react-icons/fa";

import { useNavigate, useParams } from "react-router-dom";

import {
  getOrderByIdApi,
  createReturnRequestApi,
  getUserReturnRequestsApi,
} from "../services/api";

import { useAuthStore } from "../store/authStore";

import Layout from "./Layout";
import ProfileSidebar from "./ProfileSidebar";

export default function ReturnOrder() {
  const navigate = useNavigate();
  const { orderId } = useParams();

  const user = useAuthStore((state) => state.user);

  const [order, setOrder] = useState(null);

  const [returnRequest, setReturnRequest] = useState(null);

  const [loading, setLoading] = useState(true);

  const [submitting, setSubmitting] = useState(false);

  const [error, setError] = useState("");

  const [success, setSuccess] = useState("");

  // =====================================================
  // SELECTED PRODUCTS
  // =====================================================

  const [selectedItems, setSelectedItems] = useState({});

  // =====================================================
  // RETURN REASON
  // =====================================================

  const [reason, setReason] = useState("");

  const [additionalReason, setAdditionalReason] = useState("");

  // =====================================================
  // FILES
  // =====================================================

  const [files, setFiles] = useState([]);

  // =====================================================
  // FETCH ORDER + RETURN REQUEST
  // =====================================================

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError("");

        // -------------------------------------------------
        // USER VALIDATION
        // -------------------------------------------------

        if (!user?.id) {
          setError("Please login to continue.");
          return;
        }

        // -------------------------------------------------
        // ORDER ID VALIDATION
        // -------------------------------------------------

        if (!orderId) {
          setError("Order ID is missing.");
          return;
        }

        // =================================================
        // FETCH ORDER
        // =================================================

        const orderResponse = await getOrderByIdApi(orderId);

        console.log("RETURN ORDER RESPONSE:", orderResponse);

        const orderData =
          orderResponse?.order ||
          orderResponse?.data?.order ||
          orderResponse?.data ||
          orderResponse;

        if (!orderData) {
          setError("Order not found.");
          return;
        }

        console.log("ORDER DATA:", orderData);

        setOrder(orderData);

        // =================================================
        // FETCH USER RETURN REQUESTS
        // =================================================

        try {
          const returnResponse = await getUserReturnRequestsApi(user.id);

          console.log("USER RETURN REQUESTS RESPONSE:", returnResponse);

          const returnRequests =
            returnResponse?.returnRequests ||
            returnResponse?.data?.returnRequests ||
            returnResponse?.data ||
            [];

          console.log("ALL RETURN REQUESTS:", returnRequests);

          // -------------------------------------------------
          // FIND RETURN REQUEST FOR CURRENT ORDER
          // -------------------------------------------------

          const existingReturn = Array.isArray(returnRequests)
            ? returnRequests.find(
                (request) =>
                  request?.orderId?.toString() === orderId.toString(),
              )
            : null;

          console.log("RETURN REQUEST FOR THIS ORDER:", existingReturn);

          if (existingReturn) {
            setReturnRequest(existingReturn);
          }
        } catch (returnError) {
          console.error("Failed to fetch return request:", returnError);

          // Do not stop the page if return request lookup fails.
          // The order can still be displayed.
        }

        // =================================================
        // CHECK ORDER STATUS
        // =================================================

        const orderStatus = String(
          orderData.order_status || orderData.status || "",
        ).toLowerCase();

        console.log("ORDER STATUS:", orderStatus);

        // -------------------------------------------------
        // If return already exists, do not block the page.
        // ReturnRequestDetails will be displayed.
        // -------------------------------------------------

        if (orderStatus !== "delivered" && !returnRequest) {
          // We don't immediately use returnRequest here because
          // setState is asynchronous.
          //
          // The return request is checked again below through
          // the fetched data.
        }
      } catch (err) {
        console.error("Fetch return order error:", err);

        setError(
          err.response?.data?.message || err.message || "Failed to load order.",
        );
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [orderId, user?.id]);

  // =====================================================
  // SELECT / UNSELECT ITEM
  // =====================================================

  const toggleItem = (item, index) => {
    const key = `${item.productId}-${index}`;

    setSelectedItems((prev) => {
      const updated = { ...prev };

      if (updated[key]) {
        delete updated[key];
      } else {
        updated[key] = {
          productId: item.productId,

          title: item.title,

          price: Number(item.price || 0),

          quantity: 1,

          maxQuantity: Number(item.quantity || 1),

          image: Array.isArray(item.image)
            ? item.image[0] || ""
            : item.image || "",
        };
      }

      return updated;
    });
  };

  // =====================================================
  // CHANGE RETURN QUANTITY
  // =====================================================

  const changeQuantity = (key, quantity) => {
    setSelectedItems((prev) => {
      if (!prev[key]) {
        return prev;
      }

      const newQuantity = Number(quantity);

      return {
        ...prev,

        [key]: {
          ...prev[key],

          quantity: Math.min(Math.max(newQuantity, 1), prev[key].maxQuantity),
        },
      };
    });
  };

  // =====================================================
  // FILE UPLOAD
  // =====================================================

  const handleFileChange = (e) => {
    const selectedFiles = Array.from(e.target.files || []);

    if (!selectedFiles.length) {
      return;
    }

    // ---------------------------------------------------
    // ALLOWED TYPES
    // ---------------------------------------------------

    const allowedTypes = [
      "image/jpeg",
      "image/jpg",
      "image/png",
      "image/webp",
      "video/mp4",
      "video/mpeg",
      "video/quicktime",
      "video/webm",
    ];

    const invalidFiles = selectedFiles.filter(
      (file) => !allowedTypes.includes(file.type),
    );

    if (invalidFiles.length > 0) {
      setError(
        "Only JPG, PNG, WEBP images and MP4, MPEG, MOV, WEBM videos are allowed.",
      );

      e.target.value = "";

      return;
    }

    // ---------------------------------------------------
    // MAX 7 FILES
    // ---------------------------------------------------

    if (files.length + selectedFiles.length > 7) {
      setError("You can upload a maximum of 7 files.");

      e.target.value = "";

      return;
    }

    // ---------------------------------------------------
    // MAX 50 MB PER FILE
    // ---------------------------------------------------

    const maxFileSize = 50 * 1024 * 1024;

    const oversizedFile = selectedFiles.find((file) => file.size > maxFileSize);

    if (oversizedFile) {
      setError(
        `Each image/video must be smaller than 50 MB. "${oversizedFile.name}" is too large.`,
      );

      e.target.value = "";

      return;
    }

    // ---------------------------------------------------
    // ADD FILES
    // ---------------------------------------------------

    setError("");

    setFiles((prev) => [...prev, ...selectedFiles]);

    e.target.value = "";
  };

  // =====================================================
  // REMOVE FILE
  // =====================================================

  const removeFile = (index) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  };

  // =====================================================
  // SUBMIT RETURN REQUEST
  // =====================================================

  const handleSubmit = async (e) => {
    e.preventDefault();

    setError("");

    setSuccess("");

    // ---------------------------------------------------
    // SELECTED ITEMS
    // ---------------------------------------------------

    const items = Object.values(selectedItems);

    if (!items.length) {
      setError("Please select at least one product to return.");

      return;
    }

    // ---------------------------------------------------
    // REASON
    // ---------------------------------------------------

    if (!reason) {
      setError("Please select a return reason.");

      return;
    }

    // ---------------------------------------------------
    // ADDITIONAL REASON
    // ---------------------------------------------------

    if (!additionalReason.trim()) {
      setError("Please provide some details about the return.");

      return;
    }

    // ---------------------------------------------------
    // FILE REQUIRED
    // ---------------------------------------------------

    if (!files.length) {
      setError("Please upload at least one image or video of the product.");

      return;
    }

    // ---------------------------------------------------
    // USER
    // ---------------------------------------------------

    if (!user?.id) {
      setError("User information is missing. Please login again.");

      return;
    }

    // ---------------------------------------------------
    // ORDER
    // ---------------------------------------------------

    if (!orderId) {
      setError("Order ID is missing.");

      return;
    }

    try {
      setSubmitting(true);

      // =================================================
      // FORM DATA
      // =================================================

      const formData = new FormData();

      formData.append("orderId", orderId);

      formData.append("userId", user.id);

      formData.append("reason", reason);

      formData.append("additionalReason", additionalReason.trim());

      // -------------------------------------------------
      // ITEMS
      // -------------------------------------------------

      formData.append(
        "items",
        JSON.stringify(
          items.map((item) => ({
            productId: item.productId,

            title: item.title,

            price: item.price,

            quantity: item.quantity,
          })),
        ),
      );

      // -------------------------------------------------
      // FILES
      // Backend:
      // upload.array("images", 7)
      // -------------------------------------------------

      files.forEach((file) => {
        formData.append("images", file);
      });

      // =================================================
      // DEBUG
      // =================================================

      console.log("========== RETURN REQUEST ==========");

      for (const [key, value] of formData.entries()) {
        if (value instanceof File) {
          console.log(key, {
            name: value.name,

            type: value.type,

            size: value.size,
          });
        } else {
          console.log(key, value);
        }
      }

      // =================================================
      // API
      // =================================================

      const response = await createReturnRequestApi(formData);

      console.log("RETURN REQUEST RESPONSE:", response);

      // -------------------------------------------------
      // IMPORTANT
      // Immediately display returned request
      // -------------------------------------------------

      const createdReturnRequest =
        response?.returnRequest ||
        response?.data?.returnRequest ||
        response?.data ||
        null;

      if (createdReturnRequest) {
        setReturnRequest(createdReturnRequest);
      }

      setSuccess("Return request submitted successfully.");

      // -------------------------------------------------
      // Clear form
      // -------------------------------------------------

      setSelectedItems({});

      setReason("");

      setAdditionalReason("");

      setFiles([]);
    } catch (err) {
      console.error("Return request error:", err);

      setError(
        err.response?.data?.message ||
          err.message ||
          "Failed to submit return request.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  // =====================================================
  // LOADING
  // =====================================================

  if (loading) {
    return (
      <Layout>
        <div className="max-w-5xl mx-auto px-6 py-20 text-center text-gray-500">
          Loading order...
        </div>
      </Layout>
    );
  }

  // =====================================================
  // ERROR WITHOUT ORDER
  // =====================================================

  if (error && !order) {
    return (
      <Layout>
        <div className="max-w-5xl mx-auto px-6 py-10">
          <div className="bg-red-50 border border-red-200 text-red-600 rounded-lg p-5 text-center">
            {error}
          </div>

          <button
            onClick={() => navigate("/orders")}
            className="mt-5 flex items-center gap-2 mx-auto text-light_primary"
          >
            <FaArrowLeft />
            Back to Orders
          </button>
        </div>
      </Layout>
    );
  }

  // =====================================================
  // RETURN REQUEST ALREADY EXISTS
  // =====================================================

  if (returnRequest) {
    return (
      <Layout>
        {/* =================================================
            HEADER
        ================================================= */}

        <section className="bg-[#FFF8EC] px-6 md:px-16 py-8">
          <div className="mx-auto">
            <h1 className="text-3xl md:text-4xl font-serif text-light_primary">
              Return Request
            </h1>

            <p className="text-gray-500 mt-2">
              View the details and current status of your return request.
            </p>
          </div>
        </section>

        {/* =================================================
            CONTENT
        ================================================= */}

        <section className="px-6 lg:px-12 md:px-12 sm:px-12 xs:px-10 xxs:px-8 py-6 md:py-10 lg:py-10 sm:py-10 xs:py-8 xxs:py-6 mx-auto bg-white">
          <div className="mx-auto grid grid-cols-1 md:grid-cols-[270px_1fr] gap-8">
            {/* SIDEBAR */}

            <ProfileSidebar />

            {/* MAIN CONTENT */}

            <section>
              <div className="max-w-5xl mx-auto">
                {/* BACK BUTTON */}

                <button
                  onClick={() => navigate("/orders")}
                  className="flex items-center gap-2 text-light_primary hover:underline mb-5"
                >
                  <FaArrowLeft />
                  Back to Orders
                </button>

                {/* RETURN DETAILS COMPONENT */}
              </div>
            </section>
          </div>
        </section>
      </Layout>
    );
  }

  // =====================================================
  // CHECK ORDER STATUS BEFORE SHOWING FORM
  // =====================================================

  const orderStatus = String(
    order?.order_status || order?.status || "",
  ).toLowerCase();

  if (orderStatus !== "delivered") {
    return (
      <Layout>
        <div className="max-w-5xl mx-auto px-6 py-10">
          <div className="bg-red-50 border border-red-200 text-red-600 rounded-lg p-5 text-center">
            Only delivered orders can be returned.
          </div>

          <button
            onClick={() => navigate("/orders")}
            className="mt-5 flex items-center gap-2 mx-auto text-light_primary"
          >
            <FaArrowLeft />
            Back to Orders
          </button>
        </div>
      </Layout>
    );
  }

  // =====================================================
  // RETURN FORM
  // =====================================================

  return (
    <Layout>
      {/* =================================================
          HEADER
      ================================================= */}

      <section className="bg-[#FFF8EC] px-6 md:px-16 py-8">
        <div className="mx-auto">
          <button
            onClick={() => navigate("/orders")}
            className="flex items-center gap-2 text-light_primary hover:underline mb-5"
          >
            <FaArrowLeft />
            Back to Orders
          </button>

          <h1 className="text-3xl md:text-4xl font-serif text-light_primary">
            Return Order
          </h1>

          <p className="text-gray-500 mt-2">
            Select the products you want to return and tell us why.
          </p>
        </div>
      </section>

      {/* =================================================
          CONTENT
      ================================================= */}

      <section className="px-6 lg:px-12 md:px-12 sm:px-12 xs:px-10 xxs:px-8 py-6 md:py-10 lg:py-10 sm:py-10 xs:py-8 xxs:py-6 mx-auto bg-white">
        <div className="mx-auto grid grid-cols-1 md:grid-cols-[270px_1fr] gap-8">
          {/* =================================================
              SIDEBAR
          ================================================= */}

          <ProfileSidebar />

          {/* =================================================
              MAIN CONTENT
          ================================================= */}

          <section>
            <div className="max-w-5xl mx-auto">
              {/* =================================================
                  ORDER INFORMATION
              ================================================= */}

              <div className="border border-gray-200 rounded-xl p-6 mb-6">
                <div className="flex flex-col md:flex-row md:justify-between gap-4">
                  {/* ORDER ID */}

                  <div>
                    <p className="text-sm text-gray-500">Order ID</p>

                    <p className="font-semibold text-gray-800">#{order._id}</p>
                  </div>

                  {/* ORDER DATE */}

                  <div>
                    <p className="text-sm text-gray-500">Order Date</p>

                    <p className="font-medium text-gray-800">
                      {order.createdAt
                        ? new Date(order.createdAt).toLocaleDateString("en-IN")
                        : "-"}
                    </p>
                  </div>

                  {/* STATUS */}

                  <div>
                    <p className="text-sm text-gray-500">Status</p>

                    <span className="inline-block mt-1 px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm">
                      Delivered
                    </span>
                  </div>
                </div>
              </div>

              {/* =================================================
                  FORM
              ================================================= */}

              <form onSubmit={handleSubmit}>
                {/* =================================================
                    1. SELECT PRODUCTS
                ================================================= */}

                <div className="border border-gray-200 rounded-xl p-6 mb-6">
                  <h2 className="text-xl font-semibold text-gray-800 mb-5">
                    1. Select Products to Return
                  </h2>

                  <div className="space-y-4">
                    {order.items?.map((item, index) => {
                      const key = `${item.productId}-${index}`;

                      const selected = selectedItems[key];

                      return (
                        <div
                          key={key}
                          className={`border rounded-xl p-4 transition ${
                            selected
                              ? "border-light_primary bg-orange-50"
                              : "border-gray-200"
                          }`}
                        >
                          <div className="flex gap-4 items-center">
                            {/* CHECKBOX */}

                            <input
                              type="checkbox"
                              checked={!!selected}
                              onChange={() => toggleItem(item, index)}
                              className="w-5 h-5 accent-orange-500"
                            />

                            {/* IMAGE */}

                            <div className="w-20 h-20 rounded-lg overflow-hidden border border-gray-200 flex-shrink-0">
                              {Array.isArray(item.image) && item.image[0] ? (
                                <img
                                  src={item.image[0]}
                                  alt={item.title}
                                  className="w-full h-full object-cover"
                                />
                              ) : typeof item.image === "string" &&
                                item.image ? (
                                <img
                                  src={item.image}
                                  alt={item.title}
                                  className="w-full h-full object-cover"
                                />
                              ) : (
                                <div className="w-full h-full bg-gray-100 flex items-center justify-center">
                                  <FaBoxOpen className="text-gray-400" />
                                </div>
                              )}
                            </div>

                            {/* DETAILS */}

                            <div className="flex-1">
                              <h3 className="font-medium text-gray-800">
                                {item.title}
                              </h3>

                              <p className="text-sm text-gray-500 mt-1">
                                ₹
                                {Number(item.price || 0).toLocaleString(
                                  "en-IN",
                                )}
                              </p>

                              <p className="text-sm text-gray-500">
                                Purchased quantity: {item.quantity}
                              </p>
                            </div>

                            {/* QUANTITY */}

                            {selected && (
                              <div>
                                <label className="block text-xs text-gray-500 mb-1">
                                  Return Qty
                                </label>

                                <select
                                  value={selected.quantity}
                                  onChange={(e) =>
                                    changeQuantity(key, e.target.value)
                                  }
                                  className="text-black border border-gray-300 rounded-lg px-3 py-2"
                                >
                                  {Array.from(
                                    {
                                      length: selected.maxQuantity,
                                    },
                                    (_, i) => i + 1,
                                  ).map((qty) => (
                                    <option key={qty} value={qty}>
                                      {qty}
                                    </option>
                                  ))}
                                </select>
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* =================================================
                    2. REASON
                ================================================= */}

                <div className="border border-gray-200 rounded-xl p-6 mb-6">
                  <h2 className="text-xl font-semibold text-gray-800 mb-5">
                    2. Reason for Return
                  </h2>

                  <select
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    className="w-full text-black border border-gray-300 rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-orange-200"
                  >
                    <option value="">Select a reason</option>

                    <option value="damaged">Product arrived damaged</option>

                    <option value="defective">Product is defective</option>

                    <option value="wrong_item">Wrong product received</option>

                    <option value="different_from_description">
                      Product is different from description
                    </option>

                    <option value="size_issue">Size issue</option>

                    <option value="quality_issue">Quality issue</option>

                    <option value="other">Other</option>
                  </select>

                  <textarea
                    value={additionalReason}
                    onChange={(e) => setAdditionalReason(e.target.value)}
                    placeholder="Please provide more details..."
                    rows={5}
                    className="text-black w-full border border-gray-300 rounded-lg px-4 py-3 mt-4 resize-none focus:outline-none focus:ring-2 focus:ring-orange-200"
                  />
                </div>

                {/* =================================================
                    3. ATTACHMENTS
                ================================================= */}

                <div className="border border-gray-200 rounded-xl p-6 mb-6">
                  <h2 className="text-xl font-semibold text-gray-800 mb-2">
                    3. Add Photos / Video{" "}
                    <span className="text-red-500">*</span>
                  </h2>

                  <p className="text-sm text-gray-500 mb-5">
                    Please upload at least one photo or video showing the issue
                    with the product.
                  </p>

                  <label className="border-2 border-dashed border-gray-300 rounded-xl p-8 flex flex-col items-center justify-center cursor-pointer hover:border-orange-400 transition">
                    <div className="flex gap-3 text-3xl text-gray-400 mb-3">
                      <FaImage />

                      <FaVideo />
                    </div>

                    <p className="font-medium text-gray-700">Click to upload</p>

                    <p className="text-xs text-gray-400 mt-1 text-center">
                      JPG, PNG, WEBP, MP4, MPEG, WEBM, MOV
                    </p>

                    <p className="text-xs text-gray-400 mt-1">
                      Maximum 7 files · 50 MB each
                    </p>

                    <input
                      type="file"
                      multiple
                      accept="image/jpeg,image/png,image/webp,image/jpg,video/mp4,video/mpeg,video/quicktime,video/webm"
                      onChange={handleFileChange}
                      className="hidden"
                    />
                  </label>

                  {/* FILE LIST */}

                  {files.length > 0 && (
                    <div className="mt-5 space-y-2">
                      {files.map((file, index) => (
                        <div
                          key={`${file.name}-${file.size}-${index}`}
                          className="flex items-center justify-between bg-gray-50 border border-gray-200 rounded-lg px-4 py-3"
                        >
                          <div className="flex items-center gap-3 min-w-0">
                            {file.type.startsWith("video/") ? (
                              <FaVideo className="text-gray-500 flex-shrink-0" />
                            ) : (
                              <FaImage className="text-gray-500 flex-shrink-0" />
                            )}

                            <div className="min-w-0">
                              <span className="block text-sm text-gray-700 truncate max-w-[300px]">
                                {file.name}
                              </span>

                              <span className="block text-xs text-gray-400 mt-1">
                                {(file.size / (1024 * 1024)).toFixed(2)} MB
                              </span>
                            </div>
                          </div>

                          <button
                            type="button"
                            onClick={() => removeFile(index)}
                            className="text-red-500 hover:text-red-700 ml-3 flex-shrink-0"
                            aria-label={`Remove ${file.name}`}
                          >
                            <FaTimes />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* =================================================
                    ERROR
                ================================================= */}

                {error && (
                  <div className="bg-red-50 border border-red-200 text-red-600 rounded-lg p-4 mb-5">
                    {error}
                  </div>
                )}

                {/* =================================================
                    SUCCESS
                ================================================= */}

                {success && (
                  <div className="bg-green-50 border border-green-200 text-green-600 rounded-lg p-4 mb-5">
                    {success}
                  </div>
                )}

                {/* =================================================
                    SUBMIT
                ================================================= */}

                <div className="flex justify-end">
                  <button
                    type="submit"
                    disabled={submitting}
                    className="bg-light_primary hover:bg-hover_light_primary disabled:opacity-50 disabled:cursor-not-allowed text-white px-8 py-3 rounded-lg font-medium transition"
                  >
                    {submitting ? "Submitting..." : "Submit Return Request"}
                  </button>
                </div>
              </form>
            </div>
          </section>
        </div>
      </section>
    </Layout>
  );
}
